import { Component, Input, OnChanges, OnInit, input, output, inject } from '@angular/core';
import { Employee } from '../../data/employee';
import { Process } from '../../data/process';
import { Form } from '../../data/form';
import { Approved } from '../../types/approved';
import { environment } from '../../../environments/environment';
import { Approval } from '../../data/approval';
import { EmployeeService } from '../../services/employee.service';
import { ValidEmployeeOrgUnit } from '../../data/valid-employee-org-unit';
import {
  BrowserMessagesService,
  ButtonConfirmDirective,
  ButtonConfirmType, CopyTooltipDirective,
  ExportService
} from '@rbb/ng-web-template-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { ProcessService } from '../../services/process.service';
import { ProcessAttachment } from '../../data/process-attachment';
import { UtilService } from '../../services/util.service';
import { ProcessStatus } from '../../types/process-status';
import { ApproverType } from '../../types/approver-type';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-approver-list-user-view',
  templateUrl: './approver-list-user-view.component.html',
  styleUrls: ['./approver-list-user-view.component.css'],
  imports: [FormsModule, RouterLink, DatePipe, ButtonConfirmDirective, CopyTooltipDirective]
})
export class ApproverListUserViewComponent implements OnInit, OnChanges {
  private employeeService = inject(EmployeeService);
  private browserMessagesService = inject(BrowserMessagesService);
  private processService = inject(ProcessService);
  utilService = inject(UtilService);
  private exportService = inject(ExportService);

  dataProtectionOfficer = environment.dataProtectionOfficer;
  webUrl = environment.webUrl;
  ButtonConfirmType = ButtonConfirmType;

  readonly employee = input<Employee>();
  @Input() process: Process | undefined;
  @Input() form: Form | undefined;
  readonly orgUnitChanged = output<void>();
  readonly loadingEmployee = input<boolean>();
  readonly approvalsValid = input(true);

  validOrgUnits: ValidEmployeeOrgUnit[] | undefined;
  selectedValidOrgUnit: ValidEmployeeOrgUnit | undefined;
  changingActiveOrgUnit = false;

  approved: Date | undefined;
  declined: Date | undefined;

  ngOnInit(): void {
    this.getValidOrgUnits();
    this.getApprovalDates();
  }

  ngOnChanges(): void {
    this.getApprovalDates();
  }

  getValidOrgUnits(): void {
    this.employeeService.getValidOrgUnits().subscribe({
      next: (next) => {
        this.validOrgUnits = next;
        this.selectedValidOrgUnit = this.validOrgUnits.find(item => item.active);
        if (!this.selectedValidOrgUnit) {
          this.selectedValidOrgUnit = this.validOrgUnits.find(item => item.sap);
        }
        this.changingActiveOrgUnit = false;
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Gültige Organisationseinheiten konnten nicht geladen werden');
        this.changingActiveOrgUnit = false;
      }
    });
  }

  setOrgUnitAsActive(orgUnit: ValidEmployeeOrgUnit): void {
    this.changingActiveOrgUnit = true;
    this.employeeService.setOrgUnitAsActive(orgUnit.id).subscribe({
      next: () => {
        this.getValidOrgUnits();
        // TODO: The 'emit' function requires a mandatory void argument
        this.orgUnitChanged.emit();
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Die aktive Organisationseinheit konnte nicht verändert werden.');
        this.changingActiveOrgUnit = false;
      }
    });
  }

  getApprovalDates(): void {
    if (this.process?.approvals) {
      let a = true;
      for (const approval of this.process.approvals) {
        if (approval.approved !== Approved.APPROVED) {
          a = false;
        }
        if (a && approval.approvedAt && (!this.approved || this.approved < approval.approvedAt)) {
          this.approved = approval.approvedAt;
        }
        if (approval.approved === Approved.DECLINED) {
          this.approved = undefined;
          this.declined = approval.approvedAt;
          break;
        }
      }
      if (!a) {
        this.approved = undefined;
      }
    } else {
      this.approved = undefined;
      this.declined = undefined;
    }
  }

  getPreviousApproval(i: number): Approval | undefined {
    return i > 0 && this.process?.approvals?.[i - 1] ? this.process.approvals[i - 1] : undefined;
  }

  openAttachment(attachmentId: string | undefined): void {
    if (this.process?.id && attachmentId) {
      this.processService.getAttachment(this.process.id, attachmentId).subscribe({
        next: (data) => {
          this.exportService.createDownloadableFileFromHttpResponse(data, 'antrag.pdf');
        },
        error: (error: HttpErrorResponse) => {
          this.browserMessagesService.showHttpError(error, 'Fehler');
        }
      });
    }
  }

  deleteAttachment(attachment: ProcessAttachment): void {
    this.processService.deleteAttachment(this.process?.id ?? '', attachment.id ?? '').subscribe(() => attachment.deleted = Date());
  }

  protected readonly ProcessStatus = ProcessStatus;
  protected readonly ApproverType = ApproverType;
  protected readonly Approved = Approved;
}
